import { arrow, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import classNames from 'classnames';
import { Moment } from 'moment';
import moment from 'moment';
import React, { PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import { Button, Tooltip } from '@myhealth-raccoon/components';

import { Slot, Timeslot } from '.';
import { BookingUserProfileModel } from 'src/services/booking';
import { innerHtml } from 'src/utils/html';
import { doctorAvatarSrc, doctorName } from 'src/utils/name';

type TimeslotButtonProps = {
    disabled?: boolean;
    time: Moment;
    selected?: Moment;
    onClick(ts: Slot): void;
    ts: Timeslot;
    defaultDuration?: number;
    allUsers?: BookingUserProfileModel[];
    unavailableHtml?: string;
};

const isTouchScreen = window.matchMedia('(pointer: coarse)').matches;
export function TimeslotButton({
    disabled,
    time,
    selected,
    onClick,
    ts,
    defaultDuration,
    allUsers,
    unavailableHtml,
}: TimeslotButtonProps) {
    const arrowRef = useRef(null);

    const { refs, floatingStyles, middlewareData, placement } = useFloating({
        placement: 'right',
        strategy: 'absolute',
        // transform: false,
        middleware: [offset(10), flip({ fallbackAxisSideDirection: 'end' }), shift(), arrow({ element: arrowRef })],
    });

    const defaultDurationActual = useMemo(
        () =>
            ts.slots.length === 1 || ts.slots.every((slot) => slot.duration === ts.slots[0].duration)
                ? Number(ts.slots[0].duration)
                : defaultDuration,
        [defaultDuration, ts.slots]
    );

    const ButtonWrapper: React.FC<PropsWithChildren> = useCallback(
        ({ children }) => {
            if (!isTouchScreen) {
                return (
                    <Tooltip
                        className={classNames({ 'pointer-events-none': disabled })}
                        attachmentPoint="right"
                        tooltipContent={
                            <>
                                {time.format('ddd, D MMM')}
                                <br />
                                {time.format('LT')} ({moment.duration(defaultDurationActual, 'minutes').humanize()})
                            </>
                        }
                    >
                        {children}
                    </Tooltip>
                );
            }
            return <>{children}</>;
        },
        [defaultDurationActual, disabled, time]
    );

    if (unavailableHtml) {
        return (
            <Popover>
                <ButtonWrapper>
                    <PopoverButton
                        as={Button}
                        color={selected && selected?.isSame(time, 'minute') ? 'primary' : 'gray'}
                        className={classNames('group relative w-full !px-0.5 !py-2 [@media(max-width:340px)]:text-xs', {
                            'hover:!border-primary hover:!text-primary hover:!bg-white': !selected,
                        })}
                        ref={refs.setPositionReference}
                        disabled={disabled}
                    >
                        {time.format('LT')}
                    </PopoverButton>
                </ButtonWrapper>
                <PopoverPanel
                    className={'absolute z-10 w-64 drop-shadow'}
                    ref={refs.setFloating}
                    style={floatingStyles}
                >
                    <div
                        className={classNames('absolute size-2 rotate-45 bg-white', {
                            '-left-1': placement === 'right',
                            '-right-1': placement === 'left',
                            '-top-1': placement === 'bottom',
                            '-bottom-1': placement === 'top',
                        })}
                        ref={arrowRef}
                        style={{
                            left: middlewareData.arrow?.x,
                            top: middlewareData.arrow?.y,
                        }}
                    ></div>
                    <div {...innerHtml(unavailableHtml)} className="prose prose-sm z-20 rounded bg-white p-3" />
                </PopoverPanel>
            </Popover>
        );
    }

    if (ts.slots.length > 1) {
        const popoverContent = (
            <div className="relative z-20 rounded bg-white py-3 text-xs font-medium text-gray-700">
                <div className="flex flex-col gap-2.5 px-3">
                    {time.format('ddd D MMM, LT')} ({moment.duration(defaultDurationActual, 'minutes').humanize()})
                </div>
                <hr className="my-2.5" />
                <div>
                    <p className="my-1.5 flex flex-row items-center gap-1 px-3">
                        <i className="relative m-px inline-block size-1.5 align-middle">
                            <i className="bg-success-500 block size-full rounded-full"></i>
                            <i className="bg-success-500 absolute inset-0 block size-full animate-ping rounded-full"></i>
                        </i>{' '}
                        {ts.slots.length} doctors
                    </p>
                    {ts.slots.map((t) => {
                        const user = allUsers?.find((u) => u.userId === `${t.userId}`);
                        if (!user) {
                            return null;
                        }
                        return (
                            <div
                                key={t.userId}
                                className="flex flex-row items-center gap-1 px-3 py-1.5 transition-colors hover:bg-gray-100"
                            >
                                {user && <img className="size-6 rounded-full" src={doctorAvatarSrc(user)} />}
                                <div className="flex flex-col">
                                    <strong className="text-xs font-medium">{doctorName(user)}</strong>
                                    {Number(t.duration) !== defaultDurationActual && (
                                        <span className="text-2xs font-normal">
                                            {moment.duration(t.duration, 'minutes').humanize()}
                                        </span>
                                    )}
                                </div>
                                {!user.displayProfileNoOnlineBooking ? (
                                    <Button onClick={() => onClick(t)} className="!text-2xs ml-auto !px-2.5 !py-1.5">
                                        Book
                                    </Button>
                                ) : (
                                    <Tooltip
                                        className="ml-auto"
                                        tooltipContent={
                                            <>
                                                <p>
                                                    <strong>This provider does not accept online bookings.</strong>
                                                </p>
                                                <p>
                                                    Please select another provider or contact the centre to make an
                                                    appointment
                                                </p>
                                            </>
                                        }
                                    >
                                        <Button disabled className="!text-2xs !px-2.5 !py-1.5">
                                            Book
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );

        return (
            <Popover>
                <ButtonWrapper>
                    <PopoverButton
                        as={Button}
                        color={selected && selected?.isSame(time, 'minute') ? 'primary' : 'gray'}
                        className={classNames('group relative w-full !px-0.5 !py-2 [@media(max-width:340px)]:text-xs', {
                            'hover:!border-primary hover:!text-primary hover:!bg-white': !selected,
                        })}
                        ref={refs.setPositionReference}
                        disabled={disabled}
                    >
                        {time.format('LT')}
                        <small className="text-3xs absolute right-1 top-0.5 font-normal text-gray-400 transition-colors group-disabled:text-gray-100">
                            {ts.slots.length}
                        </small>
                    </PopoverButton>
                </ButtonWrapper>
                <PopoverPanel
                    className={'absolute z-10 w-64 drop-shadow'}
                    ref={refs.setFloating}
                    style={floatingStyles}
                >
                    <div
                        className={classNames('absolute size-2 rotate-45 bg-white', {
                            '-left-1': placement === 'right',
                            '-right-1': placement === 'left',
                            '-top-1': placement === 'bottom',
                            '-bottom-1': placement === 'top',
                        })}
                        ref={arrowRef}
                        style={{
                            left: middlewareData.arrow?.x,
                            top: middlewareData.arrow?.y,
                        }}
                    ></div>
                    {popoverContent}
                </PopoverPanel>
            </Popover>
        );
    } else {
        const slot = ts.slots[0];
        return (
            <ButtonWrapper>
                <Button
                    disabled={disabled}
                    color={selected && selected?.isSame(time, 'minute') ? 'primary' : 'gray'}
                    className={classNames('group relative w-full !px-0.5 !py-2 [@media(max-width:340px)]:text-xs', {
                        'hover:!border-primary hover:!text-primary hover:!bg-white': !selected,
                    })}
                    onClick={() => onClick(slot)}
                >
                    {time.format('LT')}
                </Button>
            </ButtonWrapper>
        );
    }
}
